/**
 * Unified Zunelo chat workspace — cloud conversations (start from scratch or
 * anchored to a Slack thread), opened/created from the sidebar's
 * Conversaciones section or via "Preguntar acerca del hilo" on Slack cards.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useZunelo } from '../../context/ZuneloContext';
import { Button } from '../../ui/common/Button';
import { useResizableInspectorWidth } from '../../hooks/useResizableInspectorWidth';
import { useSheetDialog } from '../../hooks/useSheetDialog';
import { INSPECTOR_MAX_WIDTH_PX, INSPECTOR_MIN_WIDTH_PX } from '../inspector/inspectorLayout';

export type ChatAsideVariant = 'docked' | 'sheet';

interface ChatAsideProps {
  variant?: ChatAsideVariant;
}

export const ChatAside: React.FC<ChatAsideProps> = ({ variant = 'docked' }) => {
  const {
    isChatOpen,
    chatMessages,
    chatAnchor,
    activeConversation,
    closeChat,
    startNewChat,
    sendChatQuestion,
    isChatSending,
    isConversationLoading,
    chatError,
  } = useZunelo();
  const { width, isResizing, onResizePointerDown } = useResizableInspectorWidth();
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const isSheet = variant === 'sheet';
  const sheetRef = useSheetDialog<HTMLDivElement>({
    isOpen: isSheet && isChatOpen,
    onClose: closeChat,
    initialFocusSelector: '[data-testid="chat-close"]',
  });

  useEffect(() => {
    if (!isChatOpen || !listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [isChatOpen, chatMessages.length]);

  useEffect(() => {
    if (isChatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isChatOpen, activeConversation?.id]);

  if (!isChatOpen) return null;

  const submitQuestion = async (text: string) => {
    const q = text.trim();
    if (!q || isChatSending || isConversationLoading) return;
    setDraft('');
    try {
      await sendChatQuestion(q);
    } catch {
      setDraft(q);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitQuestion(draft);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void submitQuestion(draft);
    }
  };

  const hasConversation = Boolean(activeConversation);
  const composerDisabled = isChatSending || isConversationLoading;

  return (
    <div
      ref={
        isSheet
          ? (node) => {
              (sheetRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }
          : undefined
      }
      data-testid="chat-aside"
      data-variant={variant}
      aria-label="Zunelo chat"
      aria-modal={isSheet ? true : undefined}
      role={isSheet ? 'dialog' : undefined}
      tabIndex={isSheet ? -1 : undefined}
      className={isSheet ? 'z-inspector-sheet' : undefined}
      style={
        isSheet
          ? {
              boxSizing: 'border-box',
              borderLeft: 'none',
              background: 'var(--z-surface)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : {
              width,
              minWidth: INSPECTOR_MIN_WIDTH_PX,
              maxWidth: INSPECTOR_MAX_WIDTH_PX,
              flex: `0 0 ${width}px`,
              boxSizing: 'border-box',
              borderLeft: 'none',
              background: 'var(--z-surface)',
              backdropFilter: 'blur(16px)',
              position: 'relative',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transition: isResizing ? 'none' : 'width 0.15s ease, flex-basis 0.15s ease',
            }
      }
    >
      {!isSheet && (
        <div
          data-testid="chat-resize-handle"
          className="z-inspector-resize-handle"
          role="separator"
          aria-orientation="vertical"
          onPointerDown={onResizePointerDown}
        />
      )}

      <header
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 10,
          padding: '14px 16px',
          borderBottom: '1px solid var(--z-border)',
          position: 'sticky',
          top: 0,
          background: 'var(--z-surface)',
          zIndex: 1,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--z-text)' }}>
            {hasConversation ? activeConversation?.title : 'Nueva conversación'}
          </div>
          {chatAnchor ? (
            <div
              data-testid="chat-anchor"
              style={{
                marginTop: 4,
                fontSize: 11.5,
                color: 'var(--z-dim)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Anclado: {chatAnchor.channelName}
            </div>
          ) : hasConversation ? (
            <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--z-dim)' }}>
              Conversación desde cero
            </div>
          ) : (
            <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--z-dim)' }}>
              Conversaciones con seguimiento en la nube
            </div>
          )}
        </div>
        <Button size="sm" variant="ghost" onClick={() => startNewChat()} data-testid="chat-new">
          Nuevo
        </Button>
        <button
          type="button"
          data-testid="chat-close"
          onClick={closeChat}
          aria-label="Cerrar chat"
          style={{
            border: 'none',
            background: 'var(--z-fill-2)',
            borderRadius: 8,
            width: 28,
            height: 28,
            cursor: 'pointer',
            color: 'var(--z-text-2)',
          }}
        >
          ×
        </button>
      </header>

      <div
        ref={listRef}
        data-testid="chat-messages"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {hasConversation && chatMessages.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--z-dim)', lineHeight: 1.6 }}>
            Escribe abajo para empezar esta conversación.
          </div>
        ) : null}
        {chatMessages.map((msg) => (
          <div
            key={msg.id}
            data-testid={`chat-msg-${msg.role}`}
            style={{
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '92%',
              padding: '8px 11px',
              borderRadius: 12,
              background:
                msg.role === 'user'
                  ? 'var(--z-accent-soft)'
                  : msg.role === 'system'
                    ? 'var(--z-fill-2)'
                    : 'var(--z-fill)',
              color: 'var(--z-text-2)',
              fontSize: 12.5,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {msg.text}
          </div>
        ))}
        {isChatSending && <div style={{ fontSize: 12, color: 'var(--z-dim)' }}>Pensando…</div>}
        {chatError ? (
          <div
            data-testid="chat-error"
            style={{ fontSize: 12, color: 'var(--z-warn-ink)', lineHeight: 1.5 }}
          >
            {chatError}
          </div>
        ) : null}
      </div>

      <form
        onSubmit={(e) => void onSubmit(e)}
        style={{
          padding: '12px 16px 16px',
          borderTop: '1px solid var(--z-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <textarea
          ref={inputRef}
          data-testid="chat-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={chatAnchor ? 'Pregunta sobre el hilo anclado…' : 'Escribe tu pregunta…'}
          disabled={composerDisabled}
          rows={3}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            borderRadius: 10,
            border: '1px solid var(--z-border)',
            background: 'var(--z-fill)',
            color: 'var(--z-text)',
            fontFamily: 'inherit',
            fontSize: 13,
            padding: '8px 10px',
            resize: 'vertical',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            data-testid="chat-send"
            disabled={!draft.trim() || isChatSending}
            loading={isChatSending}
          >
            Preguntar
          </Button>
        </div>
      </form>
    </div>
  );
};
